import { resolveChatBotSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveChatBotSettings";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { MultiSectionLayout } from "@jugl-web/ui-kit";
import {
  SideMenuDivider,
  SideMenuList,
  SideMenuListItem,
  SideMenuPanel,
} from "@jugl-web/ui-kit/layout/SideMenuLayout";
import { useTranslations } from "@jugl-web/utils";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, Fragment, useMemo, useState } from "react";
import { ReactComponent as ChatStartersActiveIcon } from "./assets/chat-starters-active.svg";
import { ReactComponent as ChatStartersIcon } from "./assets/chat-starters.svg";
import { ReactComponent as GeneralInfoActiveIcon } from "./assets/general-info-active.svg";
import { ReactComponent as GeneralInfoIcon } from "./assets/general-info.svg";
import { ReactComponent as GeneralSetupActiveIcon } from "./assets/general-setup-active.svg";
import { ReactComponent as GeneralSetupIcon } from "./assets/general-setup.svg";
import { ReactComponent as InstallActiveIcon } from "./assets/install-active.svg";
import { ReactComponent as InstallIcon } from "./assets/install.svg";
import { ReactComponent as LauncherStyleActiveIcon } from "./assets/launcher-style-active.svg";
import { ReactComponent as LauncherStyleIcon } from "./assets/launcher-style.svg";
import { ReactComponent as QuickActionsActiveIcon } from "./assets/quick-actions-active.svg";
import { ReactComponent as QuickActionsIcon } from "./assets/quick-actions.svg";
import { ReactComponent as VoiceModeActiveIcon } from "./assets/voice-mode-active.svg";
import { ReactComponent as VoiceModeIcon } from "./assets/voice-mode.svg";
import { CTATabContent } from "./components/CTATabContent";
import { GeneralInfoTabContent } from "./components/GeneralInfoTabContent";
import { GeneralSetupTabContent } from "./components/GeneralSetupTabContent";
import { InstallTabContent } from "./components/InstallTabContent";
import { LauncherStyleTabContent } from "./components/LauncherStyleTabContent";
import { SuggestionsTabContent } from "./components/SuggestionsTabContent";
import { VoiceChatTabContent } from "./components/VoiceChatTabContent";
import { WebChatBotAccountMenuTab } from "./types";

export const WebChatBotAccountScreen: FC = () => {
  const [selectedMenuTab, setSelectedMenuTab] =
    useState<WebChatBotAccountMenuTab>(WebChatBotAccountMenuTab.generalInfo);

  const { requestNavigation } = useNavigationGuardContext();

  const { t } = useTranslations();

  const { webChatBotAccount } = useWebChatBotAccount();
  const { entity } = useEntitySelectedProvider();
  const isCxEntity = entity.type === "cx";
  const isVoiceModeEnabled = resolveChatBotSettings(webChatBotAccount?.settings)
    .voice_config.enabled;

  const sideMenuSections = useMemo(() => {
    const sections = [
      [
        {
          id: WebChatBotAccountMenuTab.generalInfo,
          label: t({
            id: "client-support-page.general-info",
            defaultMessage: "General Info",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.generalInfo ? (
              <GeneralInfoActiveIcon />
            ) : (
              <GeneralInfoIcon />
            ),
        },
      ],
      [
        {
          id: WebChatBotAccountMenuTab.generalSetup,
          label: t({
            id: "client-support-page.general-setup",
            defaultMessage: "General Setup",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.generalSetup ? (
              <GeneralSetupActiveIcon />
            ) : (
              <GeneralSetupIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.launcherStyle,
          label: t({
            id: "client-support-page.launcher-style",
            defaultMessage: "Launcher Style",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.launcherStyle ? (
              <LauncherStyleActiveIcon />
            ) : (
              <LauncherStyleIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.cta,
          label: t({
            id: "client-support-page.chat-starters",
            defaultMessage: "Chat Starters",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.cta ? (
              <ChatStartersActiveIcon />
            ) : (
              <ChatStartersIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.voiceMode,
          isHidden: !isCxEntity,
          label: t({
            id: "client-support-page.voice-mode",
            defaultMessage: "Voice mode",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.voiceMode ? (
              <VoiceModeActiveIcon />
            ) : (
              <VoiceModeIcon />
            ),
          endIcon: (
            <span className="font-secondary text-xs text-dark-600">
              {isVoiceModeEnabled
                ? t({ id: "common.on", defaultMessage: "On" })
                : t({ id: "common.off", defaultMessage: "Off" })}
            </span>
          ),
        },
        {
          id: WebChatBotAccountMenuTab.suggestions,
          label: t({
            id: "client-support-page.quick-actions",
            defaultMessage: "Quick Actions",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.suggestions ? (
              <QuickActionsActiveIcon />
            ) : (
              <QuickActionsIcon />
            ),
        },
      ],
      [
        {
          id: WebChatBotAccountMenuTab.install,
          label: t({
            id: "client-support-page.install",
            defaultMessage: "Install",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.install ? (
              <InstallActiveIcon />
            ) : (
              <InstallIcon />
            ),
        },
      ],
    ];

    return sections
      .map((section) => section.filter((item) => !item.isHidden))
      .filter((section) => section.length > 0);
  }, [isCxEntity, isVoiceModeEnabled, selectedMenuTab, t]);

  return (
    <MultiSectionLayout
      leftSide={
        <SideMenuPanel>
          {sideMenuSections.map((section, index) => {
            const isLastSection = index === sideMenuSections.length - 1;

            return (
              <Fragment key={+index}>
                <SideMenuList>
                  {section.map((item) => (
                    <SideMenuListItem
                      key={item.id}
                      label={item.label}
                      startIcon={item.startIcon}
                      endIcon={item.endIcon}
                      isSelected={selectedMenuTab === item.id}
                      onClick={() =>
                        requestNavigation(() => setSelectedMenuTab(item.id))
                      }
                    />
                  ))}
                </SideMenuList>
                {!isLastSection && <SideMenuDivider />}
              </Fragment>
            );
          })}
        </SideMenuPanel>
      }
    >
      {selectedMenuTab === WebChatBotAccountMenuTab.generalInfo && (
        <GeneralInfoTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.generalSetup && (
        <GeneralSetupTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.launcherStyle && (
        <LauncherStyleTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.cta && <CTATabContent />}
      {selectedMenuTab === WebChatBotAccountMenuTab.suggestions && (
        <SuggestionsTabContent />
      )}
      {isCxEntity && selectedMenuTab === WebChatBotAccountMenuTab.voiceMode && (
        <VoiceChatTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.install && (
        <InstallTabContent />
      )}
    </MultiSectionLayout>
  );
};
